import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useBranding } from '@/contexts/branding-context';
import { apiRequest } from '@/lib/api-client';
import { Loader2, Upload, X, Building2, Palette, FileText, Headphones, Star } from 'lucide-react';
import { Switch } from '@/components/ui/switch';

export function GymBrandingForm() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { branding, refetch } = useBranding();

  const [logoPreview, setLogoPreview] = useState<string | null>(
    branding?.logo || null,
  );
  const [logoBase64, setLogoBase64] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState(
    branding?.businessName || '',
  );
  const [primaryColor, setPrimaryColor] = useState(
    branding?.primaryColor || '#C0392B',
  );
  const [privacyPolicyName, setPrivacyPolicyName] = useState(
    branding?.privacyPolicyName || 'Privacy Policy',
  );
  const [termsAndConditionsName, setTermsAndConditionsName] = useState(
    branding?.termsAndConditionsName || 'Terms and Conditions',
  );
  const [dataDeletionName, setDataDeletionName] = useState(
    branding?.dataDeletionName || 'Data Deletion',
  );
  const [supportName, setSupportName] = useState(
    branding?.supportName || 'Support',
  );
  const [supportEmail, setSupportEmail] = useState(
    branding?.supportEmail || '',
  );
  const [supportAddress, setSupportAddress] = useState(
    branding?.supportAddress || '',
  );
  const [reviewsPublic, setReviewsPublic] = useState(
    branding?.reviewsPublic !== false,
  );
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({ title: t('common.error'), description: 'Please select an image file', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: t('common.error'), description: 'Image must be under 5MB', variant: 'destructive' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setLogoPreview(result);
      setLogoBase64(result);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  }, []);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleClearLogo = () => {
    setLogoPreview(branding?.logo || null);
    setLogoBase64(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let logoUrl = branding?.logo || '';

      // Upload new logo first if one was selected
      if (logoBase64) {
        setUploading(true);
        try {
          const uploadRes = await apiRequest<{ url: string }>({
            method: 'POST',
            url: '/tenants/branding/logo',
            data: { logo: logoBase64 },
          });
          logoUrl = uploadRes.url;
        } finally {
          setUploading(false);
        }
      }

      // Update branding (name + color + logo + legal/support)
      await apiRequest({
        method: 'PATCH',
        url: '/tenants/branding',
        data: {
          businessName: businessName.trim() || undefined,
          primaryColor,
          privacyPolicyName: privacyPolicyName.trim() || undefined,
          termsAndConditionsName: termsAndConditionsName.trim() || undefined,
          dataDeletionName: dataDeletionName.trim() || undefined,
          supportName: supportName.trim() || undefined,
          supportEmail: supportEmail.trim(),
          supportAddress: supportAddress.trim(),
          reviewsPublic,
        },
      });

      // Refresh branding context so logo/colors update everywhere
      refetch();

      toast({
        title: t('common.success'),
        description: 'Gym settings saved successfully',
      });
      setLogoBase64(null);
      if (logoUrl) setLogoPreview(logoUrl);
    } catch (err) {
      toast({
        title: t('common.error'),
        description: err instanceof Error ? err.message : 'Failed to save settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const isBusy = uploading || saving;

  return (
    <div className="space-y-8">
      {/* Logo Upload */}
      <div className="space-y-3">
        <Label className="text-base font-semibold flex items-center gap-2">
          <Upload className="h-4 w-4" />
          Gym Logo
        </Label>
        <p className="text-sm text-muted-foreground">
          Upload your gym logo. PNG, JPG, SVG or WebP · Max 5MB
        </p>

        <div
          className={`relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 transition-colors cursor-pointer
            ${isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30'}`}
          onClick={() => !isBusy && fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {logoPreview ? (
            <div className="relative">
              <img
                src={logoPreview}
                alt="Gym logo"
                className="h-24 w-auto max-w-[200px] object-contain rounded-lg"
              />
              {logoBase64 && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleClearLogo(); }}
                  className="absolute -top-2 -right-2 rounded-full bg-destructive p-0.5 text-destructive-foreground hover:bg-destructive/80"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
              <p className="mt-3 text-center text-xs text-muted-foreground">
                {logoBase64 ? 'New logo selected — click Save to upload' : 'Click or drag to replace'}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <div className="rounded-full bg-muted p-4">
                <Upload className="h-6 w-6" />
              </div>
              <span className="text-sm font-medium">Click to upload or drag & drop</span>
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {/* Business Name */}
      <div className="space-y-2">
        <Label htmlFor="businessName" className="text-base font-semibold flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          Business Name
        </Label>
        <p className="text-sm text-muted-foreground">
          Displayed on the login page and throughout the app
        </p>
        <Input
          id="businessName"
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          placeholder="e.g. Flex Gym"
          className="max-w-sm"
        />
      </div>

      {/* Legal & Support Link Names */}
      <div className="space-y-4 pt-4 border-t">
        <Label className="text-base font-semibold flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Legal & Support Links
        </Label>
        <p className="text-sm text-muted-foreground">
          Customize display names for footer links (Privacy Policy, Terms, Data Deletion, Support)
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="privacyPolicyName">Privacy Policy</Label>
            <Input
              id="privacyPolicyName"
              value={privacyPolicyName}
              onChange={(e) => setPrivacyPolicyName(e.target.value)}
              placeholder="Privacy Policy"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="termsAndConditionsName">Terms and Conditions</Label>
            <Input
              id="termsAndConditionsName"
              value={termsAndConditionsName}
              onChange={(e) => setTermsAndConditionsName(e.target.value)}
              placeholder="Terms and Conditions"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="dataDeletionName">Data Deletion</Label>
            <Input
              id="dataDeletionName"
              value={dataDeletionName}
              onChange={(e) => setDataDeletionName(e.target.value)}
              placeholder="Data Deletion"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="supportName">Support</Label>
            <Input
              id="supportName"
              value={supportName}
              onChange={(e) => setSupportName(e.target.value)}
              placeholder="Support"
            />
          </div>
        </div>
      </div>

      {/* Support Contact */}
      <div className="space-y-4">
        <Label className="text-base font-semibold flex items-center gap-2">
          <Headphones className="h-4 w-4" />
          Support Contact
        </Label>
        <p className="text-sm text-muted-foreground">
          Email and address shown on Support and Data Deletion pages (phone removed)
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="supportEmail">Support Email</Label>
            <Input
              id="supportEmail"
              type="email"
              value={supportEmail}
              onChange={(e) => setSupportEmail(e.target.value)}
              placeholder="support@yourgym.com"
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="supportAddress">Support Address</Label>
            <Input
              id="supportAddress"
              value={supportAddress}
              onChange={(e) => setSupportAddress(e.target.value)}
              placeholder="e.g. Rruga Komuna e Parisit, Tirane"
            />
          </div>
        </div>
      </div>

      {/* Reviews Visibility */}
      <div className="space-y-4 pt-4 border-t">
        <Label className="text-base font-semibold flex items-center gap-2">
          <Star className="h-4 w-4" />
          Reviews & Ratings
        </Label>
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label htmlFor="reviewsPublic" className="text-sm font-medium">
              Show ratings publicly
            </Label>
            <p className="text-sm text-muted-foreground">
              Allow members to see class and instructor ratings in the app
            </p>
          </div>
          <Switch
            id="reviewsPublic"
            checked={reviewsPublic}
            onCheckedChange={setReviewsPublic}
          />
        </div>
      </div>

      {/* Brand Color */}
      <div className="space-y-2">
        <Label htmlFor="primaryColor" className="text-base font-semibold flex items-center gap-2">
          <Palette className="h-4 w-4" />
          Brand Color
        </Label>
        <p className="text-sm text-muted-foreground">
          Primary color used for buttons, highlights and accents
        </p>
        <div className="flex items-center gap-3">
          <div className="relative h-10 w-10 overflow-hidden rounded-lg border cursor-pointer">
            <input
              id="primaryColor"
              type="color"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            />
            <div className="h-full w-full rounded-lg" style={{ backgroundColor: primaryColor }} />
          </div>
          <Input
            value={primaryColor}
            onChange={(e) => setPrimaryColor(e.target.value)}
            placeholder="#C0392B"
            className="w-32 font-mono text-sm"
            maxLength={7}
          />
          <div className="h-10 w-24 rounded-lg border flex items-center justify-center">
            <span className="text-xs font-medium" style={{ color: primaryColor }}>Preview</span>
          </div>
        </div>
      </div>

      {/* Save */}
      <div className="pt-2">
        <Button onClick={handleSave} disabled={isBusy} className="min-w-[120px]">
          {isBusy ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {uploading ? 'Uploading...' : t('common.saving')}
            </>
          ) : (
            t('common.save')
          )}
        </Button>
      </div>
    </div>
  );
}
